import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { AppError } from '../../../utils/firebaseErrors';
import type { StaleReadMeta } from '../../staleReadFallback/staleReadCache';
import type { ReportDetail, ReportReviewDecision, ReportSpecies, ReportStatus } from '../report';
import {
  getFaceScreeningMediaIdentity,
  getPhotoIdentityFromStoragePath,
} from '../reportPhotoIdentity';

export type ReportReviewActionScope =
  | 'status'
  | 'finalizeRejection'
  | 'species'
  | 'labels'
  | 'falsePositive'
  | 'removeMedia';
type FaceScreeningWorkflowSnapshot = {
  faceScreeningReviewSignals: NonNullable<ReportDetail['faceScreeningReviewSignals']>;
  faceScreeningFalsePositiveHistory: NonNullable<ReportDetail['faceScreeningFalsePositiveHistory']>;
  photos: string[];
  status: ReportStatus;
};
type StatusDecisionWorkflowSnapshot = {
  status: ReportStatus;
  confirmationFinalityAtMs?: number;
  rejectionFinalityAtMs?: number;
};
type ReportReviewOperationValue =
  | ReportStatus
  | ReportReviewDecision
  | ReportSpecies
  | string[]
  | StatusDecisionWorkflowSnapshot
  | FaceScreeningWorkflowSnapshot;
export type ReportPresenceLostReason = 'reportPresenceLost';

export interface PendingReportReviewOperation {
  operationId: string;
  reportId: string;
  scope: ReportReviewActionScope;
  targetId?: string;
  previousValue: ReportReviewOperationValue;
  nextValue: ReportReviewOperationValue;
  attempt: number;
  state: 'pending' | 'retrying';
}

export type FaceScreeningReviewOutcome =
  | {
      kind: 'falsePositiveClassified';
      reportId: string;
      mediaIdentity: string;
    }
  | {
      kind: 'flaggedMediaRemoved';
      reportId: string;
      mediaIdentity: string;
    }
  | {
      kind: 'reviewDrivenReportDeletion';
      reportId: string;
      mediaIdentity: string;
    };

export interface ReportReviewWorkflowState {
  currentReportDetail: ReportDetail | null;
  acceptedRejectionFinalizations: Record<string, true>;
  deletedReportIds: Record<string, true>;
  reportPresenceLosses: Record<string, { reason: ReportPresenceLostReason }>;
  pendingOperations: Record<string, PendingReportReviewOperation>;
  errors: Record<string, AppError>;
  lastFaceScreeningOutcome: FaceScreeningReviewOutcome | null;
}

const initialState: ReportReviewWorkflowState = {
  currentReportDetail: null,
  acceptedRejectionFinalizations: {},
  deletedReportIds: {},
  reportPresenceLosses: {},
  pendingOperations: {},
  errors: {},
  lastFaceScreeningOutcome: null,
};

const getFaceScreeningActionTargetIdentity = ({
  mediaStoragePath,
  mediaIdentity,
}: {
  mediaStoragePath: string;
  mediaIdentity?: string;
}): string =>
  getFaceScreeningMediaIdentity({
    mediaIdentity,
    mediaStoragePath,
  });

const isPhotoIdentityMatch = ({
  mediaStoragePath,
  mediaIdentity,
  targetMediaIdentity,
  fallbackMediaStoragePath,
}: {
  mediaStoragePath: string;
  mediaIdentity?: string;
  targetMediaIdentity: string;
  fallbackMediaStoragePath: string;
}): boolean => {
  const candidateIdentity = getFaceScreeningMediaIdentity({
    mediaIdentity,
    mediaStoragePath,
  });

  return (
    candidateIdentity === targetMediaIdentity ||
    mediaStoragePath === targetMediaIdentity ||
    getPhotoIdentityFromStoragePath(mediaStoragePath) ===
      getPhotoIdentityFromStoragePath(fallbackMediaStoragePath) ||
    mediaStoragePath === fallbackMediaStoragePath
  );
};

const removeReportPhotoByTargetIdentity = (
  photos: string[],
  targetMediaIdentity: string,
  mediaStoragePath: string
): string[] =>
  photos.filter(
    (photoStoragePath) =>
      !isPhotoIdentityMatch({
        mediaStoragePath: photoStoragePath,
        targetMediaIdentity,
        fallbackMediaStoragePath: mediaStoragePath,
      })
  );

const getMediaActionSnapshot = ({
  detail,
  reportId,
}: {
  detail: ReportReviewWorkflowState['currentReportDetail'];
  reportId: string;
}) => {
  if (!detail || detail.report.id !== reportId) {
    return null;
  }

  const activeSignals = detail.faceScreeningReviewSignals ?? [];
  const history = detail.faceScreeningFalsePositiveHistory ?? [];

  return {
    faceScreeningReviewSignals: [...activeSignals],
    faceScreeningFalsePositiveHistory: [...history],
    photos: [...detail.report.photos],
    status: detail.report.status,
  };
};

const operationKey = (reportId: string, scope: ReportReviewActionScope, targetId?: string) =>
  `${reportId}:${scope}:${targetId ?? ''}`;

const hasPendingOperationForReport = (
  state: ReportReviewWorkflowState,
  reportId: string
): boolean =>
  Object.values(state.pendingOperations).some((operation) => operation.reportId === reportId);

const isReportPresenceLostInState = (state: ReportReviewWorkflowState, reportId: string): boolean =>
  state.reportPresenceLosses[reportId] !== undefined;

const clearReportScopedReviewState = (state: ReportReviewWorkflowState, reportId: string): void => {
  delete state.acceptedRejectionFinalizations[reportId];

  Object.entries(state.pendingOperations).forEach(([key, operation]) => {
    if (operation.reportId === reportId) {
      delete state.pendingOperations[key];
    }
  });

  Object.keys(state.errors).forEach((key) => {
    if (key.startsWith(`${reportId}:`)) {
      delete state.errors[key];
    }
  });

  if (state.currentReportDetail?.report.id === reportId) {
    state.currentReportDetail = null;
  }

  if (state.lastFaceScreeningOutcome?.reportId === reportId) {
    state.lastFaceScreeningOutcome = null;
  }
};

const reportReviewWorkflowSlice = createSlice({
  name: 'reportReviewWorkflow',
  initialState,
  reducers: {
    reportReviewDetailLoaded(state, action: PayloadAction<ReportDetail>) {
      const detail = action.payload;
      if (isReportPresenceLostInState(state, detail.report.id)) {
        return;
      }

      if (hasPendingOperationForReport(state, detail.report.id)) {
        return;
      }

      const staleRead = (detail as ReportDetail & { staleRead?: StaleReadMeta }).staleRead;
      if (staleRead?.stale !== true) {
        delete state.acceptedRejectionFinalizations[detail.report.id];
      }

      delete state.deletedReportIds[detail.report.id];
      state.currentReportDetail = detail;
    },
    reportReviewStatusDecisionRequested: {
      reducer(
        state,
        action: PayloadAction<{
          reportId: string;
          decision: ReportReviewDecision;
          operationId: string;
        }>
      ) {
        const { reportId, decision, operationId } = action.payload;
        if (isReportPresenceLostInState(state, reportId)) {
          return;
        }

        const key = operationKey(reportId, 'status');
        const currentReport =
          state.currentReportDetail?.report.id === reportId
            ? state.currentReportDetail.report
            : null;
        const previousValue: StatusDecisionWorkflowSnapshot = {
          status: currentReport?.status ?? 'pending',
          ...(currentReport?.confirmationFinalityAtMs !== undefined
            ? { confirmationFinalityAtMs: currentReport.confirmationFinalityAtMs }
            : {}),
          ...(currentReport?.rejectionFinalityAtMs !== undefined
            ? { rejectionFinalityAtMs: currentReport.rejectionFinalityAtMs }
            : {}),
        };

        if (currentReport) {
          const statusChanged = currentReport.status !== decision;
          currentReport.status = decision;
          if (statusChanged) {
            delete currentReport.confirmationFinalityAtMs;
            delete currentReport.rejectionFinalityAtMs;
          }
        }

        state.pendingOperations[key] = {
          operationId,
          reportId,
          scope: 'status',
          targetId: undefined,
          previousValue,
          nextValue: decision,
          attempt: 0,
          state: 'pending',
        };
        delete state.errors[key];
      },
      prepare(payload: { reportId: string; decision: ReportReviewDecision }) {
        return {
          payload: {
            ...payload,
            operationId: nanoid(),
          },
        };
      },
    },
    reportRejectionFinalizationRequested: {
      reducer(
        state,
        action: PayloadAction<{
          reportId: string;
          operationId: string;
        }>
      ) {
        const { reportId, operationId } = action.payload;
        if (isReportPresenceLostInState(state, reportId)) {
          return;
        }

        if (
          state.currentReportDetail?.report.id !== reportId ||
          state.currentReportDetail.report.status !== 'rejected' ||
          state.currentReportDetail.report.rejectionFinalityAtMs !== undefined ||
          state.acceptedRejectionFinalizations[reportId] === true
        ) {
          return;
        }

        const key = operationKey(reportId, 'finalizeRejection');
        if (state.pendingOperations[key]) {
          return;
        }

        state.pendingOperations[key] = {
          operationId,
          reportId,
          scope: 'finalizeRejection',
          targetId: undefined,
          previousValue: 'rejected',
          nextValue: 'rejected',
          attempt: 0,
          state: 'pending',
        };
        delete state.errors[key];
      },
      prepare(payload: { reportId: string }) {
        return {
          payload: {
            ...payload,
            operationId: nanoid(),
          },
        };
      },
    },
    reportReviewSpeciesChangeRequested: {
      reducer(
        state,
        action: PayloadAction<{
          reportId: string;
          species: ReportSpecies;
          operationId: string;
        }>
      ) {
        const { reportId, species, operationId } = action.payload;
        if (isReportPresenceLostInState(state, reportId)) {
          return;
        }

        const key = operationKey(reportId, 'species');
        const previousValue =
          state.currentReportDetail?.report.id === reportId
            ? state.currentReportDetail.report.species
            : 'unknown';

        if (state.currentReportDetail?.report.id === reportId) {
          state.currentReportDetail.report.species = species;
        }

        state.pendingOperations[key] = {
          operationId,
          reportId,
          scope: 'species',
          targetId: undefined,
          previousValue,
          nextValue: species,
          attempt: 0,
          state: 'pending',
        };
        delete state.errors[key];
      },
      prepare(payload: { reportId: string; species: ReportSpecies }) {
        return {
          payload: {
            ...payload,
            operationId: nanoid(),
          },
        };
      },
    },
    reportReviewLabelsChangeRequested: {
      reducer(
        state,
        action: PayloadAction<{
          reportId: string;
          labels: string[];
          operationId: string;
        }>
      ) {
        const { reportId, labels, operationId } = action.payload;
        if (isReportPresenceLostInState(state, reportId)) {
          return;
        }

        const key = operationKey(reportId, 'labels');
        const previousValue =
          state.currentReportDetail?.report.id === reportId
            ? [...state.currentReportDetail.reviewMetadata.labels]
            : [];

        if (state.currentReportDetail?.report.id === reportId) {
          state.currentReportDetail.reviewMetadata.labels = labels;
        }

        state.pendingOperations[key] = {
          operationId,
          reportId,
          scope: 'labels',
          targetId: undefined,
          previousValue,
          nextValue: labels,
          attempt: 0,
          state: 'pending',
        };
        delete state.errors[key];
      },
      prepare(payload: { reportId: string; labels: string[] }) {
        return {
          payload: {
            ...payload,
            operationId: nanoid(),
          },
        };
      },
    },
    reportReviewFaceScreeningFalsePositiveRequested: {
      reducer(
        state,
        action: PayloadAction<{
          reportId: string;
          mediaStoragePath: string;
          mediaIdentity?: string;
          operationId: string;
        }>
      ) {
        const { reportId, mediaStoragePath, mediaIdentity, operationId } = action.payload;
        if (isReportPresenceLostInState(state, reportId)) {
          return;
        }

        const targetMediaIdentity = getFaceScreeningActionTargetIdentity({
          mediaStoragePath,
          mediaIdentity,
        });
        const key = operationKey(reportId, 'falsePositive', targetMediaIdentity);
        const detail = state.currentReportDetail;
        const previousValue = getMediaActionSnapshot({ detail, reportId }) ?? {
          faceScreeningReviewSignals: [],
          faceScreeningFalsePositiveHistory: [],
          photos: [],
          status: 'pending',
        };

        if (detail?.report.id === reportId) {
          const signal = previousValue.faceScreeningReviewSignals.find((candidate) =>
            isPhotoIdentityMatch({
              mediaStoragePath: candidate.mediaStoragePath,
              mediaIdentity: candidate.mediaIdentity,
              targetMediaIdentity,
              fallbackMediaStoragePath: mediaStoragePath,
            })
          );

          if (signal) {
            detail.faceScreeningReviewSignals = previousValue.faceScreeningReviewSignals.filter(
              (candidate) =>
                !isPhotoIdentityMatch({
                  mediaStoragePath: candidate.mediaStoragePath,
                  mediaIdentity: candidate.mediaIdentity,
                  targetMediaIdentity,
                  fallbackMediaStoragePath: mediaStoragePath,
                })
            );
            detail.faceScreeningFalsePositiveHistory = [
              ...previousValue.faceScreeningFalsePositiveHistory,
              {
                kind: 'face-screening-false-positive',
                mediaStoragePath: signal.mediaStoragePath,
                warningKind: signal.warningKind,
                overrideAccepted: signal.overrideAccepted,
                overrideAcceptedAtMs: signal.overrideAcceptedAtMs,
                screenedAtMs: signal.screenedAtMs,
                detectorImplementation: signal.detectorImplementation,
                detectorModelVersion: signal.detectorModelVersion,
                reviewerSummary: signal.reviewerSummary,
                detectorDetailSummary: signal.detectorDetailSummary,
                mediaIdentity: signal.mediaIdentity,
                classifiedAtMs: Date.now(),
                classifiedByUserId: 'pending-reviewer',
              },
            ];
          }
        }

        state.pendingOperations[key] = {
          operationId,
          reportId,
          scope: 'falsePositive',
          targetId: targetMediaIdentity,
          previousValue,
          nextValue: previousValue,
          attempt: 0,
          state: 'pending',
        };
        delete state.errors[key];
        state.lastFaceScreeningOutcome = null;
      },
      prepare(payload: { reportId: string; mediaStoragePath: string; mediaIdentity?: string }) {
        return {
          payload: {
            ...payload,
            operationId: nanoid(),
          },
        };
      },
    },
    reportReviewFlaggedMediaRemovalRequested: {
      reducer(
        state,
        action: PayloadAction<{
          reportId: string;
          mediaStoragePath: string;
          mediaIdentity?: string;
          operationId: string;
        }>
      ) {
        const { reportId, mediaStoragePath, mediaIdentity, operationId } = action.payload;
        if (isReportPresenceLostInState(state, reportId)) {
          return;
        }

        const targetMediaIdentity = getFaceScreeningActionTargetIdentity({
          mediaStoragePath,
          mediaIdentity,
        });
        const key = operationKey(reportId, 'removeMedia', targetMediaIdentity);
        const detail = state.currentReportDetail;
        const previousValue = getMediaActionSnapshot({ detail, reportId }) ?? {
          faceScreeningReviewSignals: [],
          faceScreeningFalsePositiveHistory: [],
          photos: [],
          status: 'pending',
        };

        if (detail?.report.id === reportId) {
          const nextPhotos = removeReportPhotoByTargetIdentity(
            detail.report.photos,
            targetMediaIdentity,
            mediaStoragePath
          );
          detail.report.photos = nextPhotos;
          detail.faceScreeningReviewSignals = previousValue.faceScreeningReviewSignals.filter(
            (candidate) =>
              !isPhotoIdentityMatch({
                mediaStoragePath: candidate.mediaStoragePath,
                mediaIdentity: candidate.mediaIdentity,
                targetMediaIdentity,
                fallbackMediaStoragePath: mediaStoragePath,
              })
          );
          if (nextPhotos.length === 0) {
            detail.report.status = 'rejected';
          }
        }

        state.pendingOperations[key] = {
          operationId,
          reportId,
          scope: 'removeMedia',
          targetId: targetMediaIdentity,
          previousValue,
          nextValue: previousValue,
          attempt: 0,
          state: 'pending',
        };
        delete state.errors[key];
        state.lastFaceScreeningOutcome = null;
      },
      prepare(payload: { reportId: string; mediaStoragePath: string; mediaIdentity?: string }) {
        return {
          payload: {
            ...payload,
            operationId: nanoid(),
          },
        };
      },
    },
    reportReviewOperationAttempted(
      state,
      action: PayloadAction<{
        reportId: string;
        scope: ReportReviewActionScope;
        targetId?: string;
        operationId: string;
        attempt: number;
      }>
    ) {
      const key = operationKey(
        action.payload.reportId,
        action.payload.scope,
        action.payload.targetId
      );
      const operation = state.pendingOperations[key];
      if (!operation || operation.operationId !== action.payload.operationId) {
        return;
      }

      operation.attempt = action.payload.attempt;
      operation.state = action.payload.attempt > 0 ? 'retrying' : 'pending';
    },
    reportReviewOperationSucceeded(
      state,
      action: PayloadAction<{
        reportId: string;
        scope: ReportReviewActionScope;
        targetId?: string;
        operationId: string;
        reportDeleted?: boolean;
      }>
    ) {
      const key = operationKey(
        action.payload.reportId,
        action.payload.scope,
        action.payload.targetId
      );
      const operation = state.pendingOperations[key];
      if (!operation || operation.operationId !== action.payload.operationId) {
        return;
      }

      if (action.payload.reportDeleted) {
        state.deletedReportIds[action.payload.reportId] = true;
        clearReportScopedReviewState(state, action.payload.reportId);
      }

      if (action.payload.scope === 'finalizeRejection') {
        state.acceptedRejectionFinalizations[action.payload.reportId] = true;
      }

      if (action.payload.scope === 'status') {
        delete state.acceptedRejectionFinalizations[action.payload.reportId];
      }

      if (action.payload.scope === 'falsePositive' && action.payload.targetId) {
        state.lastFaceScreeningOutcome = {
          kind: 'falsePositiveClassified',
          reportId: action.payload.reportId,
          mediaIdentity: action.payload.targetId,
        };
      }

      if (action.payload.scope === 'removeMedia' && action.payload.targetId) {
        state.lastFaceScreeningOutcome = action.payload.reportDeleted
          ? {
              kind: 'reviewDrivenReportDeletion',
              reportId: action.payload.reportId,
              mediaIdentity: action.payload.targetId,
            }
          : {
              kind: 'flaggedMediaRemoved',
              reportId: action.payload.reportId,
              mediaIdentity: action.payload.targetId,
            };
      }

      delete state.pendingOperations[key];
      delete state.errors[key];
    },
    reportReviewOperationFailed(
      state,
      action: PayloadAction<{
        reportId: string;
        scope: ReportReviewActionScope;
        targetId?: string;
        operationId: string;
        error: AppError;
      }>
    ) {
      const key = operationKey(
        action.payload.reportId,
        action.payload.scope,
        action.payload.targetId
      );
      const operation = state.pendingOperations[key];
      if (!operation || operation.operationId !== action.payload.operationId) {
        return;
      }

      if (state.currentReportDetail?.report.id === action.payload.reportId) {
        if (action.payload.scope === 'status') {
          const previousValue = operation.previousValue as StatusDecisionWorkflowSnapshot;
          state.currentReportDetail.report.status = previousValue.status;
          delete state.currentReportDetail.report.confirmationFinalityAtMs;
          delete state.currentReportDetail.report.rejectionFinalityAtMs;
          if (previousValue.confirmationFinalityAtMs !== undefined) {
            state.currentReportDetail.report.confirmationFinalityAtMs =
              previousValue.confirmationFinalityAtMs;
          }
          if (previousValue.rejectionFinalityAtMs !== undefined) {
            state.currentReportDetail.report.rejectionFinalityAtMs =
              previousValue.rejectionFinalityAtMs;
          }
        }
        if (action.payload.scope === 'species') {
          state.currentReportDetail.report.species = operation.previousValue as ReportSpecies;
        }
        if (action.payload.scope === 'labels') {
          state.currentReportDetail.reviewMetadata.labels = operation.previousValue as string[];
        }
        if (action.payload.scope === 'falsePositive' || action.payload.scope === 'removeMedia') {
          const previousValue = operation.previousValue as FaceScreeningWorkflowSnapshot;
          state.currentReportDetail.faceScreeningReviewSignals =
            previousValue.faceScreeningReviewSignals;
          state.currentReportDetail.faceScreeningFalsePositiveHistory =
            previousValue.faceScreeningFalsePositiveHistory;
          state.currentReportDetail.report.photos = previousValue.photos;
          state.currentReportDetail.report.status = previousValue.status;
        }
      }

      delete state.pendingOperations[key];
      state.errors[key] = action.payload.error;
    },
    reportPresenceLost(
      state,
      action: PayloadAction<{ reportId: string; reason: ReportPresenceLostReason }>
    ) {
      const { reportId, reason } = action.payload;
      state.reportPresenceLosses[reportId] = { reason };
      state.deletedReportIds[reportId] = true;
      clearReportScopedReviewState(state, reportId);
    },
  },
});

export const {
  reportPresenceLost,
  reportRejectionFinalizationRequested,
  reportReviewDetailLoaded,
  reportReviewLabelsChangeRequested,
  reportReviewFaceScreeningFalsePositiveRequested,
  reportReviewFlaggedMediaRemovalRequested,
  reportReviewOperationAttempted,
  reportReviewOperationFailed,
  reportReviewOperationSucceeded,
  reportReviewSpeciesChangeRequested,
  reportReviewStatusDecisionRequested,
} = reportReviewWorkflowSlice.actions;

export const reportReviewWorkflowReducer = reportReviewWorkflowSlice.reducer;

export const getReportReviewOperationKey = operationKey;

export const selectCurrentReportDetail = (state: {
  reportReviewWorkflow: ReportReviewWorkflowState;
}): ReportDetail | null => state.reportReviewWorkflow.currentReportDetail;

export const selectIsReportDeleted = (
  state: { reportReviewWorkflow: ReportReviewWorkflowState },
  reportId: string
): boolean =>
  state.reportReviewWorkflow.deletedReportIds[reportId] === true ||
  state.reportReviewWorkflow.reportPresenceLosses[reportId] !== undefined;

export const selectIsRejectionFinalizationAccepted = (
  state: { reportReviewWorkflow: ReportReviewWorkflowState },
  reportId: string
): boolean => state.reportReviewWorkflow.acceptedRejectionFinalizations[reportId] === true;

export const selectReportPresenceLossReason = (
  state: { reportReviewWorkflow: ReportReviewWorkflowState },
  reportId: string
): ReportPresenceLostReason | undefined =>
  state.reportReviewWorkflow.reportPresenceLosses[reportId]?.reason;

export const selectReportReviewPendingOperations = (state: {
  reportReviewWorkflow: ReportReviewWorkflowState;
}): Record<string, PendingReportReviewOperation> => state.reportReviewWorkflow.pendingOperations;

export const selectLastFaceScreeningReviewOutcome = (state: {
  reportReviewWorkflow: ReportReviewWorkflowState;
}): FaceScreeningReviewOutcome | null => state.reportReviewWorkflow.lastFaceScreeningOutcome;

export const selectReportReviewError = (
  state: { reportReviewWorkflow: ReportReviewWorkflowState },
  reportId: string,
  scope: ReportReviewActionScope,
  targetId?: string
): AppError | undefined =>
  state.reportReviewWorkflow.errors[operationKey(reportId, scope, targetId)];

export const selectReportReviewPendingOperation = (
  state: { reportReviewWorkflow: ReportReviewWorkflowState },
  reportId: string,
  scope: ReportReviewActionScope,
  targetId?: string
): PendingReportReviewOperation | undefined =>
  state.reportReviewWorkflow.pendingOperations[operationKey(reportId, scope, targetId)];
